import type { MetaFunction } from '@remix-run/node'

import { Code, Entry, ParamTable, Section } from '~/components/marketing/DocsKit'

export const meta: MetaFunction = () => [
  { title: 'CLI — Quiver Docs' },
  {
    name: 'description',
    content:
      'The quiver CLI: a scriptable getter/setter over the Quiver API. Manage files, statuses, data fields, metrics, keys and secrets from your terminal or CI.',
  },
]

export default function DocsCli() {
  return (
    <div className="space-y-14">
      <section>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-300">Reference</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">CLI</h1>
        <p className="mt-5 max-w-2xl text-lg text-zinc-400">
          The <span className="font-mono text-zinc-300">quiver</span> command is a thin, scriptable client over the same
          API — ideal for CI jobs, agents, and quick reads. Every command takes{' '}
          <span className="font-mono text-zinc-300">--format json|human</span>.
        </p>
      </section>

      <Section eyebrow="Setup" title="Install & authenticate">
        <Code label="install">{`curl -fsSL https://quiveros.ai/install.sh | sh`}</Code>
        <div className={'rounded-2xl border border-white/10 bg-white/[0.02] p-5'}>
          <p className="text-sm text-zinc-400">
            Point it at your workspace with a <span className="font-mono text-zinc-300">qvr_</span> key — via env vars or
            the persisted config (<span className="font-mono text-zinc-300">~/.config/quiver/config.json</span>). Env
            vars win.
          </p>
          <div className="mt-4">
            <Code label="auth">{`# option A — environment
export QUIVER_URL="https://quiveros.ai"
export QUIVER_API_KEY="qvr_xxxxxxxxxxxx"

# option B — persisted config
quiver config set url https://quiveros.ai
quiver config set apiKey qvr_xxxxxxxxxxxx

# don't have a key? mint one (printed once)
quiver workspace apikey create "my-agent"`}</Code>
          </div>
        </div>
      </Section>

      <Section eyebrow="Files" title="quiver file">
        <Entry header={<span className="font-mono text-sm text-zinc-100">quiver file …</span>} desc="Read and mutate files. Mutations go through the real CRDT/KPI path, so dashboards and metrics stay in sync.">
          <ParamTable
            head="Command"
            rows={[
              ['file list', '--type --status --search --limit', 'List files (filterable).'],
              ['file list --parent <id>', '--parent-field --type --status --limit --offset', 'The files linked under one parent, in link order.'],
              ['file get <id>', '', 'Full file state — fields, status, metadata.'],
              ['file data <id>', '', 'Just the current data-field values.'],
              ['file create <type> <title>', '--set f=v --status --parent --parent-field --idempotency-key --force', 'Create a file with its fields, status and parent link — atomic (all of it or none), and safe to retry (each run sends an idempotency key and retries once on a server/network error).'],
              ['file create-many --from <file|->', '--type --parent --parent-field --status --atomic --idempotency-key', 'Create many from a JSON array, 200 per request, per-item results; --atomic = all or nothing.'],
              ['file set <id> <field> <value>', '--type string|number|boolean|json', 'Set one data field.'],
              ['file set <id> <field> --add <v> / --remove <v>', 'repeatable', 'Add or remove single elements of a list field (incl. file links) — concurrent adds all land. Removing a link never deletes the linked file.'],
              ['file status <id> <status>', '', 'Move the file to a status.'],
              ['file note <id> <body>', '--shared | --private --title', 'Add a note. Private to you unless --shared.'],
              ['file tell <id> <message>', '', 'Hand a plain-language update to the file agent.'],
              ['file run <id> <message>', '--context k=v', 'Same, but streams the agent’s steps live.'],
              ['file activity <id>', '--field --since --limit', 'Field-change history.'],
              ['file schema [type]', '', 'Show a type’s fields + status workflow (omit to list types).'],
            ]}
          />
          <Code label="example">{`quiver file set $ID value 600 --type number
quiver file status $ID ended
quiver file list --type score --status ended --limit 50 --format human
quiver file create task "Cut reads over" --parent $PROJECT --parent-field tasks --status in_progress --set phase=Cutover
quiver file create-many --from tasks.json --parent $PROJECT --parent-field tasks --format human
quiver file set $PROJECT issues --add $ISSUE
quiver --version   # 0.3.0 (a1b2c3d, built 2026-09-29 14:03 UTC)`}</Code>
        </Entry>
      </Section>

      <Section eyebrow="Metrics & search" title="quiver metrics · search · tell">
        <Entry header={<span className="font-mono text-sm text-zinc-100">quiver metrics …</span>} desc="Read computed values defined in your config.">
          <ParamTable
            head="Command"
            rows={[
              ['metrics list', '', 'Available metrics (id, name, type).'],
              ['metrics get <id>', '--period YYYY|qN-YYYY|lifetime', 'One metric’s value + target.'],
              ['metrics snapshot', '--period …', 'All metrics at once.'],
            ]}
          />
          <Code label="example">{`quiver metrics get leaderboard --format human
quiver search "Alice" --type score
quiver tell "log a new score of 320 for Bob on hard"`}</Code>
        </Entry>
      </Section>

      <Section eyebrow="Workspace" title="quiver workspace">
        <Entry header={<span className="font-mono text-sm text-zinc-100">quiver workspace …</span>} desc="Workspace info, config, keys, secrets and variables.">
          <ParamTable
            head="Command"
            rows={[
              ['workspace info', '', 'Name, id, config, user count.'],
              ['workspace config [validate]', '--file', 'Print or validate the full config JSON.'],
              ['workspace apikey list|create|delete', '', 'Manage qvr_ keys (create prints once).'],
              ['workspace secret set|list|delete', '', 'Encrypted, write-only secrets (${secrets.NAME}).'],
              ['workspace var set|list|delete', '', 'Plaintext variables (${variables.NAME}).'],
            ]}
          />
        </Entry>
      </Section>

      <Section eyebrow="End to end" title="A scripted round-trip">
        <Code label="bash">{`export QUIVER_URL="https://quiveros.ai" QUIVER_API_KEY="qvr_…"

quiver workspace info --format human          # confirm the key resolves
quiver file list --type score --limit 5 --format human
ID=$(quiver file list --type score --limit 1 | jq -r '.[0].id')
quiver file set "$ID" value 999 --type number # writes a KPI
quiver metrics get leaderboard --format human # read the aggregate back`}</Code>
      </Section>
    </div>
  )
}
